import { useEffect, useRef, useState } from 'react';
import { solicitarAvisosAposSucesso } from '@/lib/avisosSucesso';
import FundoRifa from './FundoRifa';
import { fraseSorteio, pad4 } from '../utils';
import type { TalaoPublico } from '../types';

interface Props {
  numeros: number[];
  talao: TalaoPublico;
  nomeComprador?: string | null;
  referencia?: string;
  aoComprarMais: () => void;
  imagemFundo?: string | null;
}

/* ─── Confete ─────────────────────────────────────────────────────────────── */
interface Particula {
  x: number; y: number; vx: number; vy: number;
  cor: string; r: number; ang: number; vang: number; vida: number;
}

const CORES_CONFETE = ['#60a5fa', '#34d399', '#ffffff', '#a78bfa', '#fbbf24'];

function useConfete(canvasRef: React.RefObject<HTMLCanvasElement | null>, ativo: boolean) {
  useEffect(() => {
    if (!ativo) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    const particulas: Particula[] = Array.from({ length: 80 }, () => ({
      x: Math.random() * canvas.width,
      y: -20,
      vx: (Math.random() - 0.5) * 4,
      vy: Math.random() * 2 + 1.5,
      cor: CORES_CONFETE[Math.floor(Math.random() * CORES_CONFETE.length)],
      r: Math.random() * 5 + 3,
      ang: Math.random() * Math.PI * 2,
      vang: (Math.random() - 0.5) * 0.15,
      vida: 1,
    }));

    let frame = 0;
    let raf: number;

    function animar() {
      ctx!.clearRect(0, 0, canvas!.width, canvas!.height);
      frame++;

      particulas.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        p.ang += p.vang;
        p.vy += 0.03;
        p.vida = Math.max(0, 1 - frame / 90);

        ctx!.save();
        ctx!.globalAlpha = p.vida;
        ctx!.translate(p.x, p.y);
        ctx!.rotate(p.ang);
        ctx!.fillStyle = p.cor;
        ctx!.fillRect(-p.r, -p.r / 2, p.r * 2, p.r);
        ctx!.restore();
      });

      if (frame < 90) raf = requestAnimationFrame(animar);
      else ctx!.clearRect(0, 0, canvas!.width, canvas!.height);
    }

    raf = requestAnimationFrame(animar);
    return () => cancelAnimationFrame(raf);
  }, [ativo, canvasRef]);
}

/* ─── Check animado (SVG) ─────────────────────────────────────────────────── */
function CheckAnimado() {
  return (
    <div className="relative mx-auto mb-4 flex h-20 w-20 items-center justify-center">
      <svg width="80" height="80" viewBox="0 0 80 80" fill="none" aria-hidden>
        <circle cx="40" cy="40" r="38" fill="#22c55e" />
        <polyline
          points="22,42 35,55 58,28"
          stroke="white"
          strokeWidth="5"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
          style={{
            strokeDasharray: 60,
            strokeDashoffset: 0,
            animation: 'desenharCheck 500ms ease-out forwards',
          }}
        />
      </svg>
      <style>{`
        @keyframes desenharCheck {
          from { stroke-dashoffset: 60; }
          to { stroke-dashoffset: 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          @keyframes desenharCheck { from { stroke-dashoffset: 0; } }
        }
      `}</style>
    </div>
  );
}

/* ─── Geração do comprovante em canvas ────────────────────────────────────── */
async function gerarComprovante(
  talao: TalaoPublico,
  numeros: number[],
  nome: string,
  referencia: string,
): Promise<Blob | null> {
  const W = 1080;
  const H = 1350;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  await document.fonts.ready;

  // Fundo gradiente
  const grad = ctx.createLinearGradient(0, 0, 0, H);
  grad.addColorStop(0, '#1d4ed8');
  grad.addColorStop(1, '#1e3a8a');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  // Título e entidade
  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'center';
  ctx.font = 'bold 64px system-ui, sans-serif';
  ctx.fillText(talao.titulo, W / 2, 100);

  if (talao.entidade) {
    ctx.font = '40px system-ui, sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.8)';
    ctx.fillText(talao.entidade, W / 2, 158);
  }

  // Cartão branco
  const cx = 60;
  const cy = 200;
  const cw = W - 120;
  const ch = H - 280;
  const radius = 32;

  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.moveTo(cx + radius, cy);
  ctx.lineTo(cx + cw - radius, cy);
  ctx.quadraticCurveTo(cx + cw, cy, cx + cw, cy + radius);
  ctx.lineTo(cx + cw, cy + ch - radius);
  ctx.quadraticCurveTo(cx + cw, cy + ch, cx + cw - radius, cy + ch);
  ctx.lineTo(cx + radius, cy + ch);
  ctx.quadraticCurveTo(cx, cy + ch, cx, cy + ch - radius);
  ctx.lineTo(cx, cy + radius);
  ctx.quadraticCurveTo(cx, cy, cx + radius, cy);
  ctx.closePath();
  ctx.fill();

  // Conteúdo do cartão
  const px = cx + 60;
  let py = cy + 60;

  // Header cartão
  ctx.fillStyle = '#6b7280';
  ctx.font = 'bold 28px system-ui, sans-serif';
  ctx.textAlign = 'left';
  ctx.fillText('COMPROVANTE DE PARTICIPAÇÃO', px, py);
  py += 50;

  // Linha divisória
  ctx.strokeStyle = '#e5e7eb';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(px, py);
  ctx.lineTo(W - px, py);
  ctx.stroke();
  py += 40;

  // Nome
  ctx.fillStyle = '#111827';
  ctx.font = 'bold 52px system-ui, sans-serif';
  ctx.textAlign = 'left';
  ctx.fillText(nome, px, py);
  py += 70;

  // Tiles dos números
  const totalNums = numeros.length;
  const porLinha = totalNums > 12 ? 6 : 4;
  const tileW = totalNums > 12 ? 120 : 150;
  const tileH = totalNums > 12 ? 58 : 72;
  const gap = 12;
  const totalLargura = porLinha * tileW + (porLinha - 1) * gap;
  const startX = cx + (cw - totalLargura) / 2;

  numeros.forEach((num, idx) => {
    const col = idx % porLinha;
    const row = Math.floor(idx / porLinha);
    const tx = startX + col * (tileW + gap);
    const ty = py + row * (tileH + gap);

    // Tile verde gradiente
    const tileGrad = ctx.createLinearGradient(tx, ty, tx, ty + tileH);
    tileGrad.addColorStop(0, '#22c55e');
    tileGrad.addColorStop(1, '#16a34a');
    ctx.fillStyle = tileGrad;
    ctx.beginPath();
    ctx.roundRect(tx, ty, tileW, tileH, 12);
    ctx.fill();

    ctx.fillStyle = '#ffffff';
    ctx.font = `bold ${totalNums > 12 ? 28 : 36}px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.fillText(pad4(num), tx + tileW / 2, ty + tileH / 2 + (totalNums > 12 ? 10 : 12));
  });

  const linhasNums = Math.ceil(numeros.length / porLinha);
  py += linhasNums * (tileH + gap) + 30;

  // Info abaixo dos tiles
  ctx.textAlign = 'left';
  ctx.fillStyle = '#374151';
  ctx.font = '34px system-ui, sans-serif';
  const qtd = `${numeros.length} número${numeros.length === 1 ? '' : 's'} · ${numeros.length} × R$ ${talao.valor_cota.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
  ctx.fillText(qtd, px, py);
  py += 44;

  ctx.fillStyle = '#6b7280';
  ctx.font = '30px system-ui, sans-serif';
  ctx.fillText(`Vendido por: ${talao.vendedor}`, px, py);
  py += 40;

  const agora = new Date();
  const dataHora = `${String(agora.getDate()).padStart(2, '0')}/${String(agora.getMonth() + 1).padStart(2, '0')}/${agora.getFullYear()} às ${String(agora.getHours()).padStart(2, '0')}:${String(agora.getMinutes()).padStart(2, '0')}`;
  ctx.fillText(dataHora, px, py);
  py += 40;

  if (referencia) {
    const ref = referencia.slice(-8).toUpperCase();
    ctx.fillText(`Ref.: ${ref}`, px, py);
    py += 40;
  }

  // Linha divisória
  py += 10;
  ctx.strokeStyle = '#e5e7eb';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(px, py);
  ctx.lineTo(W - px, py);
  ctx.stroke();
  py += 36;

  // Frase do sorteio
  ctx.fillStyle = '#1d4ed8';
  ctx.font = 'bold 30px system-ui, sans-serif';
  ctx.textAlign = 'left';
  const frase = fraseSorteio(talao);
  // quebra de linha simples
  const palavras = frase.split(' ');
  let linha = '';
  const maxLargurar = cw - 120;
  for (const palavra of palavras) {
    const teste = linha ? `${linha} ${palavra}` : palavra;
    if (ctx.measureText(teste).width > maxLargurar && linha) {
      ctx.fillText(linha, px, py);
      py += 38;
      linha = palavra;
    } else {
      linha = teste;
    }
  }
  if (linha) ctx.fillText(linha, px, py);

  // Rodapé
  ctx.fillStyle = 'rgba(255,255,255,0.7)';
  ctx.font = '28px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('Se Liga Católico · seligacatolico.com.br', W / 2, H - 50);

  return new Promise<Blob | null>((resolve) => {
    canvas.toBlob((b) => resolve(b), 'image/png');
  });
}

/* ─── Componente principal ────────────────────────────────────────────────── */
export default function PassoPronto({
  numeros,
  talao,
  nomeComprador,
  referencia = '',
  aoComprarMais,
  imagemFundo,
}: Props) {
  const [montado, setMontado] = useState(false);
  const [checkMostrado, setCheckMostrado] = useState(false);
  const [compartilhandoWhats, setCompartilhandoWhats] = useState(false);
  const [baixando, setBaixando] = useState(false);
  const [imagemBlob, setImagemBlob] = useState<Blob | null>(null);
  const [imagemDataUrl, setImagemDataUrl] = useState<string | null>(null);
  const canvasConfeteRef = useRef<HTMLCanvasElement | null>(null);
  const [confeteAtivo, setConfeteAtivo] = useState(false);

  const primeiroNome = nomeComprador
    ? nomeComprador.trim().split(/\s+/)[0]
    : null;

  // Entrada suave
  useEffect(() => {
    const t1 = setTimeout(() => setMontado(true), 30);
    const t2 = setTimeout(() => setCheckMostrado(true), 80);
    const t3 = setTimeout(() => setConfeteAtivo(true), 200);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  useConfete(canvasConfeteRef, confeteAtivo);

  // Números comprados: pede os avisos (1,5 s) uma vez, na tela de sucesso.
  useEffect(() => {
    if (talao.titulo) solicitarAvisosAposSucesso(talao.titulo);
  }, [talao.titulo]);

  // Gera a imagem do comprovante
  useEffect(() => {
    const nome = nomeComprador ?? 'Participante';
    gerarComprovante(talao, numeros, nome, referencia).then((blob) => {
      if (!blob) return;
      setImagemBlob(blob);
      const url = URL.createObjectURL(blob);
      setImagemDataUrl(url);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function baixarOuCompartilhar() {
    setBaixando(true);
    try {
      const blob = imagemBlob ?? await gerarComprovante(talao, numeros, nomeComprador ?? 'Participante', referencia);
      if (!blob) return;

      const arquivo = new File([blob], `numeros-${numeros.map(pad4).join('-')}.png`, { type: 'image/png' });
      const texto = `Comprovante · ${talao.titulo}\nNúmeros: ${numeros.map(pad4).join(', ')}\n${fraseSorteio(talao)}`;

      if (navigator.canShare?.({ files: [arquivo] })) {
        try {
          await navigator.share({ files: [arquivo], title: talao.titulo, text: texto });
          return;
        } catch (e) {
          if ((e as Error).name === 'AbortError') return;
        }
      }

      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = arquivo.name;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
    } finally {
      setBaixando(false);
    }
  }

  async function enviarWhatsApp() {
    setCompartilhandoWhats(true);
    try {
      const blob = imagemBlob ?? await gerarComprovante(talao, numeros, nomeComprador ?? 'Participante', referencia);
      if (!blob) return;

      const arquivo = new File([blob], `numeros-${numeros.map(pad4).join('-')}.png`, { type: 'image/png' });
      const texto = `Comprovante · ${talao.titulo}\nNúmeros: ${numeros.map(pad4).join(', ')}\n${fraseSorteio(talao)}\n\nReveja sua compra: ${window.location.href}`;

      if (navigator.canShare?.({ files: [arquivo] })) {
        try {
          await navigator.share({ files: [arquivo], title: talao.titulo, text: texto });
          return;
        } catch (e) {
          if ((e as Error).name === 'AbortError') return;
        }
      }

      const textoSemArquivo = `Comprovante · ${talao.titulo}\nNúmeros: ${numeros.map(pad4).join(', ')}\n${fraseSorteio(talao)}`;
      window.open(`https://wa.me/?text=${encodeURIComponent(textoSemArquivo)}`, '_blank');
    } finally {
      setCompartilhandoWhats(false);
    }
  }

  function compartilharRifa() {
    const texto = `Compra um número na ${talao.titulo} e ajuda ${talao.entidade ?? 'o evento'}!`;
    if (navigator.share) {
      navigator.share({ url: window.location.href, title: talao.titulo, text: texto }).catch(() => {});
    } else {
      navigator.clipboard.writeText(window.location.href).catch(() => {});
    }
  }

  const frase = fraseSorteio(talao);
  const cardClasses = 'bg-white/85 backdrop-blur-md rounded-3xl shadow-xl p-5 md:p-6';

  return (
    <FundoRifa imagemUrl={imagemFundo}>
      {/* Canvas do confete — cobre tela inteira */}
      <canvas
        ref={canvasConfeteRef}
        aria-hidden
        className="pointer-events-none fixed inset-0 z-20 h-full w-full"
        style={{ mixBlendMode: 'screen' }}
      />

      <div
        className="relative z-10 mx-auto w-full max-w-[560px] px-4 py-6 pb-8 transition-all duration-[220ms] ease-out"
        style={{
          opacity: montado ? 1 : 0,
          transform: montado ? 'translateY(0)' : 'translateY(12px)',
        }}
      >
        {/* Cabeçalho de gratidão */}
        <div className="mb-5 text-center">
          {checkMostrado && <CheckAnimado />}
          <h1 className="text-3xl font-bold text-white drop-shadow-lg">
            {primeiroNome ? `Deus te abençoe, ${primeiroNome}!` : 'Pagamento confirmado!'}
          </h1>
          <p className="mt-2 text-base text-white/90 drop-shadow">
            Você acabou de ajudar{' '}
            <strong>{talao.entidade ?? 'o acampamento'}</strong>.
          </p>
        </div>

        {/* Cartão dos números */}
        <div className={`${cardClasses} mb-3`}>
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-secondary-500">
            Seus números
          </p>

          <div className="flex flex-wrap gap-2">
            {numeros.map((n, idx) => (
              <div
                key={n}
                className="flex h-14 w-16 items-center justify-center rounded-2xl bg-gradient-to-b from-green-400 to-green-600 text-xl font-bold text-white shadow-sm"
                style={{
                  animation: `entrarTile 300ms ${idx * 60}ms ease-out both`,
                }}
              >
                {pad4(n)}
              </div>
            ))}
          </div>

          <div className="mt-4 space-y-1 text-xs text-secondary-500">
            <p>Vendido por {talao.vendedor}</p>
            <p>{frase}</p>
            <p className="font-medium text-foreground-800">Boa sorte na ação entre amigos!</p>
          </div>
        </div>

        {/* Preview do comprovante */}
        {imagemDataUrl && (
          <div className={`${cardClasses} mb-3`}>
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-secondary-500">
              Seu comprovante
            </p>
            <img
              src={imagemDataUrl}
              alt="Comprovante de participação"
              className="w-full rounded-2xl border border-secondary-200"
            />
          </div>
        )}

        {/* Botões de ação */}
        <div className="space-y-3">
          <button
            type="button"
            onClick={baixarOuCompartilhar}
            disabled={baixando}
            className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-gradient-to-r from-primary-500 to-primary-600 px-6 py-4 text-base font-bold text-white shadow-lg shadow-primary-500/30 transition-all hover:from-primary-600 hover:to-primary-700 disabled:cursor-not-allowed disabled:opacity-70"
          >
            <i className={baixando ? 'ri-loader-4-line animate-spin' : 'ri-download-2-line'} />
            {baixando ? 'Preparando…' : 'Baixar comprovante'}
          </button>

          <button
            type="button"
            onClick={enviarWhatsApp}
            disabled={compartilhandoWhats}
            className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-[#25D366] px-6 py-4 text-base font-bold text-white shadow-md transition-all hover:bg-[#1ebe5d] disabled:cursor-not-allowed disabled:opacity-70"
          >
            <i className={compartilhandoWhats ? 'ri-loader-4-line animate-spin' : 'ri-whatsapp-line'} />
            {compartilhandoWhats ? 'Abrindo…' : 'Enviar no WhatsApp'}
          </button>

          <button
            type="button"
            onClick={aoComprarMais}
            className="w-full cursor-pointer whitespace-nowrap rounded-2xl px-6 py-3 text-sm font-medium text-white/80 underline transition-opacity hover:opacity-80"
          >
            Comprar mais números
          </button>
        </div>

        {/* Cartão de compartilhar a rifa */}
        <div className={`${cardClasses} mt-4 flex items-start gap-3`}>
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-600">
            <i className="ri-heart-line text-lg" />
          </span>
          <div className="flex-1">
            <p className="text-sm font-medium text-foreground-800">
              Ajude <strong>{talao.vendedor}</strong> a vender mais: compartilhe esta ação com alguém.
            </p>
            <button
              type="button"
              onClick={compartilharRifa}
              className="mt-2 flex cursor-pointer items-center gap-1.5 rounded-lg bg-primary-500 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-primary-600"
            >
              <i className="ri-share-line" />
              Compartilhar ação
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes entrarTile {
          from { opacity: 0; transform: scale(0.8); }
          to   { opacity: 1; transform: scale(1); }
        }
        @media (prefers-reduced-motion: reduce) {
          @keyframes entrarTile { from { opacity: 1; } }
          canvas[aria-hidden] { display: none; }
        }
      `}</style>
    </FundoRifa>
  );
}